import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Grid,
  useTheme,
  IconButton,
  Tooltip,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Avatar,
  Divider,
  InputBase,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Article as ArticleIcon,
  LocalOffer as TagIcon,
  FolderSpecial as CollectionIcon,
  AttachMoney as MoneyIcon,
  Add as AddIcon,
  Star as StarIcon,
  Settings as SettingsIcon,
  Business as BrandIcon,
  SupervisorAccount as CurationIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { useBrand } from '../context/BrandContext';
import { useAuth } from '../context/AuthContext';
import BrandFormDialog from '../components/molecules/BrandFormDialog/BrandFormDialog';

const formatNumber = (num) => {
  if (!num || num === 0) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

const formatCurrency = (value) => {
  if (!value || value === 0) return 'R$ 0';
  if (value >= 1000000) return 'R$ ' + (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (value >= 1000) return 'R$ ' + (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return 'R$ ' + value.toFixed(0);
};

// Gera uma cor derivada do nome da marca (consistente)
const getBrandColor = (name, primaryColor) => {
  if (!name) return primaryColor;
  const colors = [
    '#6366f1', '#8b5cf6', '#06b6d4', '#10b981',
    '#f59e0b', '#ef4444', '#ec4899', '#3b82f6'
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

// Card de marca — estilo alinhado ao sidebar
const BrandCard = ({ brand, stats, onSelect, onSettings, isDefault, showOwner }) => {
  const theme = useTheme();

  const articleCount = stats?.articles || 0;
  const tagCount = stats?.tags || 0;
  const collectionCount = stats?.collections || 0;
  const totalValuation = stats?.totalValuation || 0;

  const brandColor = getBrandColor(brand.name, theme.palette.primary.main);
  const initial = (brand.name || '?')[0].toUpperCase();

  return (
    <Card
      elevation={0}
      onClick={() => onSelect(brand)}
      sx={{
        cursor: 'pointer',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: isDefault ? 'primary.main' : 'divider',
        borderRadius: 2,
        transition: 'all 0.18s',
        bgcolor: 'background.paper',
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[3],
        },
      }}
    >
      {/* Header do card — avatar com initial */}
      <Box
        sx={{
          p: 2.5,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {/* Avatar com logo ou inicial */}
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: `${brandColor}20`,
            border: `1px solid ${brandColor}40`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {brand.logo ? (
            <img
              src={brand.logo}
              alt={brand.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ color: brandColor, lineHeight: 1 }}
            >
              {initial}
            </Typography>
          )}
        </Box>

        {/* Nome + chips */}
        <Box flex={1} minWidth={0}>
          <Typography
            variant="body1"
            fontWeight={700}
            noWrap
            color="text.primary"
          >
            {brand.name}
          </Typography>
          {showOwner && brand.userId?.name && (
            <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mb: 0.5 }}>
              👤 {brand.userId.name}
            </Typography>
          )}
          {isDefault && (
            <Chip
              icon={<StarIcon sx={{ fontSize: '0.75rem !important' }} />}
              label="Padrão"
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, mt: 0.3 }}
            />
          )}
        </Box>

        {/* Botão config */}
        <Tooltip title="Configurações da marca" arrow>
          <IconButton
            onClick={(e) => { e.stopPropagation(); onSettings(brand); }}
            size="small"
            sx={{
              color: 'text.disabled',
              '&:hover': { color: 'text.secondary' }
            }}
          >
            <SettingsIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Métricas */}
      <Box
        sx={{
          px: 2.5,
          pb: 2,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <ArticleIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {formatNumber(articleCount)}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <TagIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {formatNumber(tagCount)}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <CollectionIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {formatNumber(collectionCount)}
          </Typography>
        </Box>
        {totalValuation > 0 && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <MoneyIcon sx={{ fontSize: 14, color: 'success.main' }} />
            <Typography variant="caption" color="success.main" fontWeight={700}>
              {formatCurrency(totalValuation)}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

// Card de nova marca
const NewBrandCard = ({ onClick }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2.5,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        color: 'text.secondary',
        bgcolor: 'background.paper',
        transition: 'all 0.18s',
        '&:hover': {
          borderColor: 'primary.main',
          color: 'primary.main',
          bgcolor: 'action.hover',
        },
      }}
    >
      <Box
        sx={{
          width: 44, height: 44, borderRadius: 2,
          border: '1px dashed', borderColor: 'inherit',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AddIcon sx={{ fontSize: 22 }} />
      </Box>
      <Typography variant="body1" fontWeight={600} color="inherit">
        Nova Marca
      </Typography>
    </Card>
  );
};

const TierProfileInfo = {
  small: { label: 'Pequena', emoji: '🏪', description: 'Ate 10k seguidores' },
  medium: { label: 'Media', emoji: '🏢', description: 'Menos de 100k seguidores' },
  large: { label: 'Grande', emoji: '🏛️', description: 'Acima de 100k seguidores' }
};

const BrandSelectionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { brands, loading, fetchBrands, setActiveBrand, getBrandStats, createBrand, isAdminOrMensure } = useBrand();
  const [brandStats, setBrandStats] = useState({});
  
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);

  // Separar marcas próprias e de clientes
  const ownBrands = useMemo(() => {
    if (!brands.length || !user) return [];
    if (!isAdminOrMensure) return brands;
    return brands.filter(b => {
      const ownerId = b.userId?._id || b.userId;
      return String(ownerId) === String(user._id);
    });
  }, [brands, user, isAdminOrMensure]);

  const groupedClientBrands = useMemo(() => {
    if (!isAdminOrMensure || !brands.length || !user) return null;
    
    const ownIds = new Set(ownBrands.map(b => b._id));
    const otherBrands = brands.filter(b => !ownIds.has(b._id));
    
    if (otherBrands.length === 0) return null;

    const groups = {};
    otherBrands.forEach(brand => {
      const userId = brand.userId?._id || brand.userId || 'unknown';
      const userName = brand.userId?.name || 'Cliente';
      const userEmail = brand.userId?.email || '';
      
      if (!groups[userId]) {
        groups[userId] = {
          user: { _id: userId, name: userName, email: userEmail },
          brands: []
        };
      }
      groups[userId].brands.push(brand);
    });

    Object.values(groups).forEach(g => {
      g.brands.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
    });

    return Object.values(groups).sort((a, b) =>
      a.user.name.localeCompare(b.user.name, 'pt-BR', { sensitivity: 'base' })
    );
  }, [brands, ownBrands, isAdminOrMensure, user]);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  useEffect(() => {
    if (isAdminOrMensure && createDialogOpen && allUsers.length === 0) {
      const fetchUsers = async () => {
        try {
          const res = await api.get('/users/all');
          if (res.data.success) {
            setAllUsers(res.data.data);
          }
        } catch (err) {
          console.error("Erro ao carregar usuários:", err);
        }
      };
      fetchUsers();
    }
  }, [isAdminOrMensure, createDialogOpen, allUsers.length]);

  useEffect(() => {
    const loadStats = async () => {
      if (brands.length === 0) return;
      const stats = {};
      for (const brand of brands) {
        const result = await getBrandStats(brand._id);
        if (result.success) stats[brand._id] = result.data.stats;
      }
      setBrandStats(stats);
    };
    loadStats();
  }, [brands, getBrandStats]);

  const handleSelectBrand = (brand) => { setActiveBrand(brand._id); navigate('/'); };
  const handleSettings = (brand) => { setActiveBrand(brand._id); navigate('/brand-settings'); };
  
  const handleNewBrand = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateBrand = async (formData) => {
    setIsCreating(true);
    
    const payload = {
      name: formData.name.trim(),
      tierProfile: formData.tierProfile
    };

    if (isAdminOrMensure) {
      payload.userId = formData.userId;
      if (formData.logo?.trim()) payload.logo = formData.logo.trim();
    }
    
    const result = await createBrand(payload);

    setIsCreating(false);

    if (result.success) {
      setCreateDialogOpen(false);
    } else {
      alert(result.message || 'Erro ao criar marca');
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6, px: { xs: 2, sm: 4 } }}>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box
            sx={{
              width: 48, height: 48, borderRadius: 2,
              bgcolor: 'action.selected',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 2,
            }}
          >
            <BrandIcon sx={{ fontSize: 24, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Selecione uma Marca
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Escolha a marca que deseja gerenciar
          </Typography>

          {isAdminOrMensure && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate('/brands')}
                sx={{ 
                  color: 'text.secondary', 
                  borderColor: 'divider',
                  textTransform: 'none', 
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  '&:hover': { bgcolor: 'action.hover', borderColor: 'text.secondary' }
                }}
              >
                Painel Completo de Marcas
              </Button>
            </Box>
          )}
        </Box>

        {/* Minhas Marcas */}
        <Box sx={{ mb: 6 }}>
          {isAdminOrMensure && ownBrands.length > 0 && (
            <Typography variant="body2" fontWeight={700} color="text.secondary" textTransform="uppercase" sx={{ mb: 2, letterSpacing: '0.05em' }}>
              Minhas Marcas
            </Typography>
          )}
          
          <Grid container spacing={1.5}>
            {ownBrands.map((brand) => (
              <Grid item xs={12} sm={6} key={brand._id}>
                <BrandCard
                  brand={brand}
                  stats={brandStats[brand._id]}
                  onSelect={handleSelectBrand}
                  onSettings={handleSettings}
                  isDefault={brand.isDefault}
                  showOwner={false}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <NewBrandCard onClick={handleNewBrand} />
            </Grid>
          </Grid>
        </Box>

        {/* Marcas de Clientes (modo Curadoria) */}
        {groupedClientBrands && groupedClientBrands.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              justifyContent: 'space-between', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 3, 
              pb: 1.5, 
              borderBottom: '1px solid', 
              borderColor: 'divider' 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CurationIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" fontWeight={800} color="text.secondary" textTransform="uppercase" letterSpacing="0.05em">
                  Marcas de Clientes — {groupedClientBrands.length} Cliente{groupedClientBrands.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              {/* Campo de Busca (Aparece se houver mais de 3 clientes) */}
              {groupedClientBrands.length > 3 && (
                <Box sx={{ 
                  display: 'flex', alignItems: 'center', gap: 1, 
                  bgcolor: 'action.hover', borderRadius: 2, px: 2, py: 0.5,
                  width: { xs: '100%', sm: 300 },
                  transition: 'background-color 0.2s',
                  '&:hover, &:focus-within': { bgcolor: 'action.selected' }
                }}>
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <InputBase
                    placeholder="Buscar cliente, email ou marca..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    sx={{ fontSize: '0.85rem', flex: 1 }}
                  />
                </Box>
              )}
            </Box>

            {groupedClientBrands
              .filter(group =>
                !clientSearch ||
                group.user.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                group.user.email.toLowerCase().includes(clientSearch.toLowerCase()) ||
                group.brands.some(b => b.name.toLowerCase().includes(clientSearch.toLowerCase()))
              )
              .map((group) => (
              <Box key={group.user._id} sx={{ mb: 5 }}>
                <Box sx={{ 
                  display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5,
                  bgcolor: 'action.hover', px: 2, py: 1, borderRadius: 2
                }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: 'primary.main', fontWeight: 700 }}>
                    {group.user.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                      {group.user.name}
                    </Typography>
                    {group.user.email && (
                      <Typography variant="caption" color="text.secondary">
                        {group.user.email}
                      </Typography>
                    )}
                  </Box>
                  <Chip 
                    label={`${group.brands.length} marca${group.brands.length !== 1 ? 's' : ''}`} 
                    size="small" 
                    color="default"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: 'background.paper' }} 
                  />
                </Box>
                
                <Grid container spacing={1.5} sx={{ pl: { xs: 0, sm: 2 } }}>
                  {group.brands
                    // Filtrar marcas internamente se a busca coincidir com uma marca específica e não com o cliente
                    .filter(b => 
                      !clientSearch || 
                      group.user.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                      group.user.email.toLowerCase().includes(clientSearch.toLowerCase()) ||
                      b.name.toLowerCase().includes(clientSearch.toLowerCase())
                    )
                    .map((brand) => (
                    <Grid item xs={12} sm={6} key={brand._id}>
                      <BrandCard
                        brand={brand}
                        stats={brandStats[brand._id]}
                        onSelect={handleSelectBrand}
                        onSettings={handleSettings}
                        isDefault={brand.isDefault}
                        showOwner={false} // já está no cabeçalho
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        )}

        {/* Gerenciar marcas (Apenas Administradores) */}
        {isAdminOrMensure && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="text"
              size="small"
              startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/brands')}
              sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500 }}
            >
              Gerenciar Marcas
            </Button>
          </Box>
        )}
      </Box>

      {/* Dialog - Criar Nova Marca (Padronizado) */}
      <BrandFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateBrand}
        isCreating={isCreating}
        isAdminOrMensure={isAdminOrMensure}
        allUsers={allUsers}
      />
    </Box>
  );
};

export default BrandSelectionPage;
